import get from 'lodash/get';
import moment from 'moment';

import env from '../../env';
import { FEATURE_FLAG_NAMES, SCHOOL_TERM } from '../../types/hubEnums';
import { InitialState, SchoolHolidayTerm } from '../../types/hubTypes';
import { SPECIAL_DATE_TYPE } from '../../util/constants';
import { getCurrentSchoolHoliday, getCurrentTerm, getNearestTerm, getNextTerm } from '../../util/helper/schoolTerm';
import { getFeatureFlag, getFeatureFlagPayload } from '../featureFlagsSelector';

export const getSchoolCurrentTerm = (
  state: InitialState,
  terms: Array<SchoolHolidayTerm>,
  overrideCurrentDate?: string
): SchoolHolidayTerm => {
  const isTermOverrideEnabled = getFeatureFlag(state, FEATURE_FLAG_NAMES.OVERRIDE_CURRENT_SCHOOL_TERM);
  if (isTermOverrideEnabled && env.REACT_APP_POSTHOG_ALLOW_TERM_OVERRIDE) {
    return getFeatureFlagPayload(state, FEATURE_FLAG_NAMES.OVERRIDE_CURRENT_SCHOOL_TERM) as SchoolHolidayTerm;
  }
  return getCurrentTerm(terms, overrideCurrentDate);
};

export const getSchoolCurrentHoliday = (
  state: InitialState,
  terms?: Array<SchoolHolidayTerm>,
  overrideCurrentDate?: string
): SchoolHolidayTerm | undefined => {
  const isHolidayOverrideEnabled = getFeatureFlag(state, FEATURE_FLAG_NAMES.OVERRIDE_CURRENT_SCHOOL_HOLIDAY);
  if (isHolidayOverrideEnabled && env.REACT_APP_POSTHOG_ALLOW_HOLIDAY_OVERRIDE) {
    return getFeatureFlagPayload(state, FEATURE_FLAG_NAMES.OVERRIDE_CURRENT_SCHOOL_HOLIDAY) as SchoolHolidayTerm;
  }
  return getCurrentSchoolHoliday(terms || getSchoolTermsPublicHolidays(state), overrideCurrentDate);
};

export const getSchoolNextTerm = (
  state: InitialState,
  terms: Array<SchoolHolidayTerm>,
  overrideCurrentDate?: string
): SchoolHolidayTerm => {
  const currentTerm = getSchoolCurrentTerm(state, terms, overrideCurrentDate);
  return getNextTerm(terms, overrideCurrentDate, currentTerm);
};

export const getSchoolTermsPublicHolidays = (state) => get(state, 'hubSessionsPage.schoolTermsPublicHolidays', []);

export const getUpcomingTerms = (state): Array<SchoolHolidayTerm> => {
  const terms = getSchoolTermsPublicHolidays(state);
  const schoolTerms = terms.filter((term) => {
    return (term.type || '').toLowerCase() === SPECIAL_DATE_TYPE.SCHOOL_TERM;
  });
  return schoolTerms;
};

export const getCurrentTerm_deprecated = (state): undefined | SchoolHolidayTerm => {
  const terms = getUpcomingTerms(state);
  const currentDate = moment().utc();
  const currentTerm = terms.find((term) => {
    const startDate = moment(term.startDate);
    const endDate = moment(term.endDate);
    return currentDate.isBetween(startDate, endDate.add(1, 'days'));
  });
  return currentTerm;
};

export const getIsSummer = (state) => {
  const { summerStart, summerEnd } = getSummerDates(state);
  return moment.utc().isSameOrAfter(summerStart) && moment.utc().isSameOrBefore(summerEnd);
};

export const getSummerDates = (state) => {
  try {
    const upcomingTerms = getUpcomingTerms(state);
    const term4 = getNearestTerm({ upcomingTerms, term: SCHOOL_TERM.TERM_4, order: 'desc' });
    const term1 = getNearestTerm({ upcomingTerms, term: SCHOOL_TERM.TERM_1, order: 'asc' });

    if (!term4) {
      return {
        summerStart: null,
        summerEnd: null,
      };
    }
    const summerStart = moment.utc(term4.endDate).add(1, 'day');
    const summerEnd = moment.utc(term1.startDate).subtract(1, 'day');
    return {
      summerStart,
      summerEnd,
    };
  } catch (e) {
    return {
      summerStart: null,
      summerEnd: null,
    };
  }
};

export const allowHolidayPause = (state) => {
  const terms = getSchoolTermsPublicHolidays(state);
  const currentTerm = getSchoolCurrentTerm(state as InitialState, terms);
  const isSummer = getIsSummer(state);
  /* pausing during summer is handled by the SummerPause component */
  if (isSummer) {
    return false;
  }
  if (currentTerm) {
    const endDate = moment(currentTerm.endDate);
    const currentDate = moment();
    const diff = endDate.diff(currentDate, 'days');
    /*
      summer pause will be handled by the `SummerPause` component;
      thus, if the upcoming holiday is summer, we should not allow the user to pause via
      the standard pause form, but allow them to do so via the aforementioned component
    */
    if (diff < 14 && currentTerm.schoolTerm !== SCHOOL_TERM.TERM_4) {
      return true;
    }
    return false;
  }
  return true;
};

// SUMMER PAUSING IS ENABLED FROM NOV. 4 ONWARDS
export const allowSummerHolidayPause = (state) => {
  console.warn(!!state && '[debug] allowSummerHolidayPause disabled');
  return false;
  // const schoolTerms = getUpcomingTerms(state);
  // const currentTerm = getSchoolCurrentTerm(state as InitialState, schoolTerms);
  // const nextTerm = getSchoolNextTerm(state as InitialState, schoolTerms);
  // const isSummer = getIsSummer(state);

  // const isTerm4 = currentTerm?.schoolTerm === SCHOOL_TERM.TERM_4;
  // // isSummer -> it is term 4 OR there is no ongoing term and the next term is term 1
  // const pausingForSummer = isTerm4 || (!currentTerm && nextTerm?.schoolTerm === SCHOOL_TERM.TERM_1) || isSummer;

  // const term1 = nextTerm;
  // const summerPausePromoStartDate = moment.utc('2024-11-03'); // TODO: change to Nov. 4 2024 when releasing
  // const summerPausePromoEndDate = moment(term1?.startDate).subtract(1, 'day');

  // const isDuringSummerPausePromo = moment()
  //   .utc()
  //   .isBetween(summerPausePromoStartDate, summerPausePromoEndDate, 'D', '[]');

  // // hopefully we will have migrated to Hub 2.0 by next year - else this will need to be addressed before summer next year (2025)
  // return isDuringSummerPausePromo && pausingForSummer;
};

// WE SHOW THE SUMMER HOLIDAY BANNER 4 WEEKS BEFORE THE END OF THE TERM
export const showSummerHolidayPauseBanner = (state) => {
  console.warn(!!state && '[debug] showSummerHolidayPauseBanner disabled');
  return false;
  // const schoolTerms = getUpcomingTerms(state);
  // const currentTerm = getSchoolCurrentTerm(state as InitialState, schoolTerms);
  // const nextTerm = getSchoolNextTerm(state as InitialState, schoolTerms);
  // const isSummer = getIsSummer(state);

  // const isTerm4 = currentTerm?.schoolTerm === SCHOOL_TERM.TERM_4;
  // // isSummer -> it is term 4 OR there is no ongoing term and the next term is term 1
  // const pausingForSummer = isTerm4 || (!currentTerm && nextTerm?.schoolTerm === SCHOOL_TERM.TERM_1) || isSummer;
  // const endDate = moment(currentTerm?.endDate);

  // const today = moment();
  // const diff = endDate.diff(today, 'days');
  // const SHOW_WEEKS_BEFORE_TERM_END = 4;
  // const daysBeforeTermEndToShowBanner = SHOW_WEEKS_BEFORE_TERM_END * 7;
  // const isTermEnding = diff < daysBeforeTermEndToShowBanner;

  // return pausingForSummer && isTermEnding;
};
